import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  getCohortPlaygroundProjects,
  getTeacherCohortRoster,
  type CohortPlaygroundProjectSummary,
  type TeacherRosterEnrollment,
} from "../auth/api-client";
import { useAuth } from "../auth/AuthContext";

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

/** Teacher read-only view of one student's playground projects within a cohort. */
export default function CohortStudentPlaygroundsPage() {
  const { cohortId, studentUserId } = useParams<{ cohortId: string; studentUserId: string }>();
  const { getBearerToken } = useAuth();
  const bearer = getBearerToken();

  const [projects, setProjects] = useState<CohortPlaygroundProjectSummary[]>([]);
  const [student, setStudent] = useState<TeacherRosterEnrollment | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!bearer || !cohortId || !studentUserId) return;
    setError(null);
    setLoading(true);
    try {
      const [playgroundData, rosterData] = await Promise.all([
        getCohortPlaygroundProjects(bearer, cohortId),
        getTeacherCohortRoster(bearer, cohortId, { limit: 200, offset: 0 }),
      ]);
      setProjects(playgroundData.projects.filter((p) => p.ownerUserId === studentUserId));
      setStudent(
        rosterData.enrollments.find((e) => e.studentUserId === studentUserId) ?? null
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load student playgrounds");
    } finally {
      setLoading(false);
    }
  }, [bearer, cohortId, studentUserId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const studentLabel = useMemo(() => {
    if (!student) return studentUserId ?? "Student";
    return student.email ?? student.googleSub ?? student.studentUserId;
  }, [student, studentUserId]);

  return (
    <main className="page page-align-start">
      <div className="card card-wide">
        <header className="row-header">
          <div>
            <p className="eyebrow">Cohort · Student playgrounds</p>
            <h1>{studentLabel}</h1>
            <p className="lede">
              {projects.length} playground{projects.length === 1 ? "" : "s"} in this cohort.
            </p>
          </div>
          <div className="actions inline">
            <Link to={`/teacher/cohorts/${cohortId ?? ""}`} className="btn secondary">
              ← Cohort
            </Link>
          </div>
        </header>

        {error ? (
          <div className="warn-banner" role="alert">
            {error}
          </div>
        ) : null}

        {loading ? (
          <p className="muted">Loading playgrounds…</p>
        ) : projects.length === 0 ? (
          <p className="muted">This student has no playgrounds yet.</p>
        ) : (
          <table className="teacher-table">
            <thead>
              <tr>
                <th>Playground</th>
                <th>Files</th>
                <th>Last updated</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {projects.map((p) => (
                <tr key={p.id}>
                  <td>
                    <strong>{p.title}</strong>
                  </td>
                  <td>{p.fileCount}</td>
                  <td>{formatTimestamp(p.updatedAt)}</td>
                  <td className="teacher-table-actions">
                    <Link
                      to={`/teacher/cohorts/${cohortId ?? ""}/playground/${p.id}`}
                      className="btn secondary tight"
                      style={{ textDecoration: "none" }}
                    >
                      View
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </main>
  );
}
